import { axios } from '@argon/app/axios'
import { objectToArray, toFilterString, stampItem } from '@argon/app/helpers'

const NAMESPACE = 'boards'
const ALLBOARDS = 'allBoards'
const KANBAN = 'kanban'
const TABLE = 'table'

const MATCHER = /entity_type\:eq\:(\w+)/

const state = {
  displayType: null,
  board: null,
  boardFilter: null,
  boards: [],
  boardsCount: null,
  archivedBoards: [],
  collectionURL: null,
  columns: [],
  entityType: null,
  stateAttr: null,
  flowId: null,
  listEntityType: 'allBoards',
  principals: [],
  selectableTypes: [],
  startingStates: [],
  title: '',
  transitions: [],
  anonymous: process.env.VUE_APP_ARGON_ANONYMOUS_ATTRIBUTION
    ? process.env.VUE_APP_ARGON_ANONYMOUS_ATTRIBUTION === 'true'
    : false
}

const mergeFilters = (orig, extra) => {
  let fString = orig ? (extra ? `${orig},${extra}` : orig) : null
  return { filters: fString }
}

const mutations = {
  SET_BOARD: (state, payload) => {
    state.board = payload
  },
  SET_TRANSITIONS: (state, payload) => {
    state.transitions = payload
  },
  SET_PRINCIPALS: (state, payload) => {
    state.principals = payload
  },
  SET_BOARD_ITEM_TYPE: (state, payload) => {
    state.entityType = payload
  },
  SET_BOARD_STATE_ATTR: (state, payload) => {
    state.stateAttr = payload
  },
  SET_LIST_BOARD_ITEM_TYPE: (state, payload) => {
    state.listEntityType = payload
  },
  SET_SELECTABLE_TYPES: (state, payload) => {
    state.selectableTypes = payload
  },
  SET_BOARD_FILTER: (state, payload) => {
    state.boardFilter = payload
  },
  SET_STARTING_STATES: (state, payload) => {
    state.startingStates = payload
  },
  SET_FLOW_ID: (state, payload) => {
    state.flowId = payload
  },
  SET_COLLECTION_URL: (state, payload) => {
    state.collectionURL = payload
  },
  SET_COLUMNS: (state, payload) => {
    state.columns = payload
  },
  SET_BOARD_ARCHIVED_ITEMS: (state, payload) => {
    state.boardArchivedItems = payload
  },
  SET_BOARDS: (state, payload) => {
    state.boards = payload
  },
  SET_BOARDS_COUNT: (state, payload) => {
    state.boardsCount = payload
  },
  SET_ARCHIVED_BOARDS: (state, payload) => {
    state.archivedBoards = payload
  },
  SET_TITLE(state, title) {
    state.title = title
  },
  SET_DISPLAY_TYPE(state, payload) {
    state.displayType = payload
  }
}

const actions = {
  setDisplayType: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      commit('SET_DISPLAY_TYPE', payload)
      resolve(true)
    })
  },
  setBoardTypeFilter: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      if (payload) {
        commit('SET_BOARD_FILTER', `entity_type:eq:${payload}`)
        commit('SET_LIST_BOARD_ITEM_TYPE', payload)
      } else {
        commit('SET_BOARD_FILTER', null)
      }
      resolve(payload)
    })
  },
  listBoards: ({ commit, state, rootState }, payload) => {
    const { pager } = payload
    const filters = toFilterString(rootState.app.appliedFilters[NAMESPACE])
    let filterParams = mergeFilters(state.boardFilter, filters)
    let params = { include: 'users', ...filterParams, ...pager }
    return new Promise((resolve, reject) => {
      axios({
        url: '/boards',
        params,
        method: 'GET'
      })
        .then(({ data }) => {
          commit('SET_BOARDS', data.items)
          commit('SET_BOARDS_COUNT', data.count)
          // TODO: this is where we need to retrive the available entityTypes.
          commit('SET_SELECTABLE_TYPES', [])
          resolve(data.items)
        })
        .catch(reject)
    })
  },
  listArchivedBoards: ({ commit, rootState }, payload) => {
    const filters = toFilterString(rootState.app.appliedFilters[NAMESPACE])
    let params = filters ? { include: 'only_archived', filters } : { include: 'only_archived' }
    return new Promise((resolve, reject) => {
      axios({
        url: '/boards',
        params,
        method: 'GET'
      })
        .then(({ data }) => {
          commit('SET_ARCHIVED_BOARDS', data.items)
          // TODO: this is where we need to retrive the available entityTypes.
          resolve(data.items)
        })
        .catch(reject)
    })
  },
  createBoard: ({ dispatch }, data) => {
    let url = '/boards'
    return new Promise((resolve, reject) => {
      axios({
        url,
        data,
        method: 'POST'
      })
        .then(({ data }) => {
          dispatch('listBoards', {}).then(() => {
            resolve(data)
          })
        })
        .catch(reject)
    })
  },
  getBoard: ({ commit, state }, boardRef) => {
    return new Promise((resolve, reject) => {
      axios({
        url: `/boards/${boardRef}`,
        params: { include: 'starting_states,users' },
        method: 'GET'
      })
        .then(({ data }) => {
          commit('SET_BOARD', data)
          commit('SET_TITLE', data.display)
          commit('SET_COLLECTION_URL', data.uris.entities)
          commit('SET_BOARD_ITEM_TYPE', data.entity_type)
          commit('SET_BOARD_STATE_ATTR', data.state_attr)
          commit('SET_STARTING_STATES', data.starting_states)
          commit('SET_FLOW_ID', data.flow_id)
          commit('SET_TRANSITIONS', objectToArray(data.transitions))
          commit('SET_COLUMNS', objectToArray(data.states))
          resolve(data)
        })
        .catch(reject)
    })
  },
  patchBoard: ({ commit, dispatch }, payload) => {
    const { refname, data } = payload
    return new Promise((resolve, reject) => {
      axios({
        url: `/boards/${refname}`,
        data,
        method: 'PATCH'
      })
        .then(({ data }) => {
          commit('SET_BOARD', data)
          commit('SET_TITLE', data.display)
          resolve(data)
        })
        .catch(reject)
    })
  },
  archiveBoards: ({ commit, state }, paths) => {
    let promises = paths.map((base) => {
      let url = `${base}/archive`
      return axios({ url, data: {}, method: 'POST' })
    })
    return new Promise((resolve, reject) => {
      Promise.all(promises)
        .then(() => {
          resolve(true)
        })
        .catch(reject)
    })
  },
  restoreBoards: ({ commit, state }, paths) => {
    let promises = paths.map((base) => {
      let url = `${base}/restore`
      return axios({ url, data: {}, method: 'POST' })
    })
    return new Promise((resolve, reject) => {
      Promise.all(promises)
        .then(() => {
          resolve(true)
        })
        .catch(reject)
    })
  },
  deleteBoard: ({ commit, state }, boardRef) => {
    return new Promise((resolve, reject) => {
      axios({
        url: `/boards/${boardRef}`,
        method: 'DELETE'
      })
        .then(() => {
          resolve(true)
        })
        .catch(reject)
    })
  },
  deleteBoards: ({ commit, state }, paths) => {
    return new Promise((resolve, reject) => {
      let promises = paths.map((url) => axios({ url, method: 'DELETE' }))
      Promise.all(promises)
        .then(() => {
          resolve(true)
        })
        .catch(reject)
    })
  },
  clearBoard: ({ commit }) => {
    return new Promise((resolve, reject) => {
      commit('SET_BOARD', null)
      commit('SET_TITLE', '')
      commit('SET_BOARD_ITEM_TYPE', null)
      commit('SET_BOARD_STATE_ATTR', null)
      commit('SET_FLOW_ID', null)
      commit('SET_TRANSITIONS', [])
      commit('SET_COLUMNS', [])
      resolve(true)
    })
  },
  clearBoards: ({ commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      commit('SET_BOARDS', [])
      commit('SET_LIST_BOARD_ITEM_TYPE', 'allBoards')
      commit('SET_BOARD_FILTER', null)
      dispatch('clearBoard')
      resolve(true)
    })
  },
  clearArchivedBoards: ({ commit }) => {
    return new Promise((resolve, reject) => {
      commit('SET_ARCHIVED_BOARDS', [])
      resolve(true)
    })
  },
  listPrincipals: ({ commit, dispatch }, boardRef) => {
    let url = `/boards/${boardRef}/principals`
    return new Promise((resolve, reject) => {
      axios({ url, method: 'GET', params: { include: 'attribution,type' } })
        .then(({ data }) => {
          commit('SET_PRINCIPALS', data.items)
          resolve(data.items)
        })
        .catch(reject)
    })
  },
  addPrincipals: ({ dispatch, state }, payload) => {
    const { principalIds, attribution, boardRef } = payload
    let promises = principalIds.map((principal_id) => {
      let url = `/boards/${boardRef}/principals`
      let data = { principal_id, attribution }
      return axios({
        url,
        data,
        method: 'POST'
      })
    })
    return new Promise((resolve, reject) => {
      Promise.all(promises)
        .then(() => {
          return Promise.all([dispatch('getBoard', boardRef), dispatch('listPrincipals', boardRef)])
        })
        .then(resolve)
        .catch(reject)
    })
  },
  deletePrincipals: ({ dispatch }, payload) => {
    const { principalIds, boardRef } = payload
    let promises = principalIds.map((principal_id) =>
      axios({
        url: `/boards/${boardRef}/principals/${principal_id}`,
        method: 'DELETE'
      })
    )
    return new Promise((resolve, reject) => {
      Promise.all(promises)
        .then(() => {
          return Promise.all([dispatch('getBoard', boardRef), dispatch('listPrincipals', boardRef)])
        })
        .then(resolve)
        .catch(reject)
    })
  }
}

const getters = {
  columns: (state) => state.columns,
  transitions: (state) => state.transitions,
  tableBoardItems: (state) => state.tableBoardItems,
  kanbanBoardItems: (state) => (flowState) => state.kanbanItems[flowState] || [],
  board: (state) => state.board,
  title: (state) => state.title,
  displayType: (state) => state.displayType,
  entityType: (state) => state.entityType
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
export { NAMESPACE, KANBAN, TABLE, ALLBOARDS }
